import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { BiMoney, BiSearch } from 'react-icons/bi';
import { listAccounts } from '@/api/accounts.api';
import {
  DataTable,
  type DataTableColumn,
  type DataTableSortState,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { ProgressBar } from '@/components/common/ProgressBar';
import { formatCurrencyEGP, formatDateTime } from '@/lib/format';
import type { AccountResponse, PaginatedAccounts } from '@/types/shared';
import './AccountsListPage.css';

export function AccountsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<DataTableSortState>({ field: null, direction: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [data, setData] = useState<PaginatedAccounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Debounce the free-text search box before it drives the `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // A search change re-starts pagination at page 1 (sort changes keep the current page).
  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const sortParam = sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined;

    listAccounts({
      page,
      pageSize,
      sort: sortParam,
      search: search || undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('accounts.error.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, sort, search, t, reloadToken]);

  const hasActiveSearch = Boolean(search);

  const columns: DataTableColumn<AccountResponse>[] = [
    {
      key: 'studentCode',
      header: t('accounts.columns.studentCode'),
      sortField: 'studentCode',
      // Primary identifier column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => (
        <Link to={`/accounts/${row.student.studentCode}`} className="fa-code-pill fa-ltr-isolate">
          {row.student.studentCode}
        </Link>
      ),
    },
    {
      key: 'planNumber',
      header: t('accounts.columns.planNumber'),
      render: (row) => <bdi>{t('accounts.detail.plans.planNumber', { no: row.planNumber })}</bdi>,
    },
    {
      key: 'nameAr',
      header: t('accounts.columns.nameAr'),
      sortField: 'nameAr',
      render: (row) => row.student.nameAr,
    },
    {
      key: 'nameEn',
      header: t('accounts.columns.nameEn'),
      sortField: 'nameEn',
      render: (row) => row.student.nameEn,
    },
    {
      key: 'totalAgreedFees',
      header: t('accounts.columns.totalAgreedFees'),
      sortField: 'totalAgreedFees',
      cellClassName: 'fa-accounts-list__amount-cell',
      render: (row) => <bdi>{formatCurrencyEGP(row.totalAgreedFees, i18n.language)}</bdi>,
    },
    {
      key: 'totalPaid',
      header: t('accounts.columns.totalPaid'),
      cellClassName: 'fa-accounts-list__amount-cell',
      render: (row) => <bdi>{formatCurrencyEGP(row.totalPaid, i18n.language)}</bdi>,
    },
    {
      key: 'totalRemaining',
      header: t('accounts.columns.totalRemaining'),
      cellClassName: 'fa-accounts-list__amount-cell',
      render: (row) => <bdi>{formatCurrencyEGP(row.totalRemaining, i18n.language)}</bdi>,
    },
    {
      key: 'progress',
      header: t('accounts.columns.progress'),
      render: (row) => (
        <span className="fa-accounts-list__progress-cell">
          <ProgressBar
            value={row.totalPaid}
            max={row.totalAgreedFees}
            label={t('accounts.progressValue', {
              paid: formatCurrencyEGP(row.totalPaid, i18n.language),
              total: formatCurrencyEGP(row.totalAgreedFees, i18n.language),
            })}
          />
          <bdi>
            {t('accounts.progressValue', {
              paid: formatCurrencyEGP(row.totalPaid, i18n.language),
              total: formatCurrencyEGP(row.totalAgreedFees, i18n.language),
            })}
          </bdi>
        </span>
      ),
    },
    {
      key: 'audit',
      header: t('accounts.columns.audit'),
      sortField: 'createdAt',
      render: (row) => (
        <span className="fa-accounts-list__audit">
          <span className="fa-accounts-list__audit-name">
            {row.updatedBy?.fullName ?? t('accounts.unknownUser')}
          </span>
          <bdi className="fa-accounts-list__audit-date">
            {formatDateTime(row.updatedAt, i18n.language)}
          </bdi>
        </span>
      ),
    },
  ];

  return (
    <section className="fa-accounts-list">
      <div className="fa-accounts-list__header">
        <div>
          <h1 className="fa-accounts-list__title">{t('accounts.listTitle')}</h1>
          <p className="fa-accounts-list__subtitle">
            {data ? t('accounts.resultCount', { count: data.meta.total }) : ' '}
          </p>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.student.studentCode}
        caption={t('accounts.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((v) => v + 1)}
        onRowClick={(row) => navigate(`/accounts/${row.student.studentCode}`)}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('accounts.searchPlaceholder'),
          ariaLabel: t('accounts.searchAriaLabel'),
        }}
        sort={sort}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        exportConfig={{
          endpoint: '/accounts/export',
          // Mirrors the list call's active filters, minus page/pageSize.
          params: {
            sort: sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined,
            search: search || undefined,
          },
          fallbackFilename: 'Accounts_export.xlsx',
        }}
        columnVisibility={{
          storageKey: 'accounts-table-columns',
          ariaLabel: t('table.columns.toggleButtonAriaLabel'),
          drawerTitle: t('table.columns.drawerTitleDefault'),
        }}
        emptyState={
          hasActiveSearch ? (
            <EmptyState
              icon={BiSearch}
              title={t('accounts.empty.noMatchesTitle')}
              hint={t('accounts.empty.noMatchesHint')}
              action={
                <button
                  type="button"
                  className="fa-btn fa-btn--secondary"
                  onClick={() => setSearchInput('')}
                >
                  {t('accounts.empty.clearSearch')}
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={BiMoney}
              title={t('accounts.empty.noRecordsTitle')}
              hint={t('accounts.empty.noRecordsHint')}
            />
          )
        }
      />
    </section>
  );
}
